import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Lock, Eye, EyeOff, LogIn, Loader2, Store, Shield, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const ROLES = [
  { key: "user", label: "Customer", icon: Store,
    on: "bg-blue-600 text-white shadow-md shadow-blue-600/25 ring-blue-600", off: "bg-blue-50 text-blue-800 ring-blue-100 hover:bg-blue-100/70" },
  { key: "salesman", label: "Staff", icon: UserCheck,
    on: "bg-teal-700 text-white shadow-md shadow-teal-700/25 ring-teal-700", off: "bg-teal-50 text-teal-800 ring-teal-100 hover:bg-teal-100/70" },
  { key: "admin", label: "Admin", icon: Shield,
    on: "bg-indigo-900 text-white shadow-md shadow-indigo-900/25 ring-indigo-900", off: "bg-indigo-50 text-indigo-900 ring-indigo-100 hover:bg-indigo-100/70" },
];

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  // Role-specific links (/admin, /staff, /customer) show only that role's login.
  const linkRole = { customer: "user", user: "user", staff: "salesman", salesman: "salesman", admin: "admin" }[params.get("role")];
  const [role, setRole] = useState(() => {
    if (linkRole) return linkRole;
    const saved = localStorage.getItem("slm_login_role");
    return ROLES.some((r) => r.key === saved) ? saved : "user";
  });
  const pickRole = (key) => {
    setRole(key);
    setError("");
    localStorage.setItem("slm_login_role", key);
    // Choosing Customer switches to the dedicated Customer Login layout (same as /customer link).
    if (key === "user") setParams({ role: "customer" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowPassword(false);
    setError("");
    setLoading(true);
    try {
      const user = await login(phone, password, role);
      navigate(user.role === "admin" ? "/admin" : user.role === "salesman" ? "/staff" : "/dashboard");
    } catch (err) {
      const status = err.response?.status;
      if (!err.response || status >= 500) {
        setError("Server is busy or updating. Please wait a minute and try again.");
      } else {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#E8F0FC] via-[#F1ECFA] to-[#FBF5E9] px-4 py-10"
    >
      <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-violet-200/40 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-10 left-10 h-40 w-40 rounded-full bg-amber-100/60 blur-2xl" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div data-testid="login-card" className="overflow-hidden rounded-3xl border border-white/80 bg-gradient-to-b from-white via-white to-[#F7F5FD] shadow-[0_20px_50px_-12px_rgba(30,64,175,0.22)] px-7 py-9 backdrop-blur-sm sm:px-10 sm:py-11">
          <div aria-hidden className="-mx-7 -mt-9 mb-7 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-400 to-violet-400 sm:-mx-10 sm:-mt-11" />
          <div className="flex flex-col items-center text-center">
            <img
              data-testid="slm-logo"
              src="/slm-logo.png"
              alt="SLM - Sri Lakshmi Agencies"
              className="h-24 w-auto object-contain select-none drop-shadow-sm"
              draggable={false}
            />
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-[#1E3A8A]">
              Sri Lakshmi Agencies
            </h2>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.25em] text-indigo-400">Ramakuppam</p>
          </div>

          <div className="my-7 h-px w-full bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />

          <h1 data-testid="login-heading" className="text-center text-lg font-semibold text-blue-900">
            {linkRole ? `${ROLES.find((r) => r.key === linkRole).label} Login` : "Welcome to Sri Lakshmi Agencies"}
          </h1>
          {linkRole && (() => {
            const r = ROLES.find((x) => x.key === linkRole);
            const Icon = r.icon;
            return (
              <div className="mt-3 flex justify-center">
                <span data-testid="login-role-badge" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold ring-1 ${r.on}`}>
                  <Icon className="h-4 w-4" /> {r.label}
                </span>
              </div>
            );
          })()}

          <form onSubmit={handleSubmit} method="post" action="/login" autoComplete="on" data-testid="login-form" className="mt-6 space-y-4">
            {!linkRole && <div data-testid="login-role-tabs" role="tablist" className="grid grid-cols-3 gap-2">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const on = role === r.key;
                return (
                  <button key={r.key} type="button" role="tab" aria-selected={on} data-testid={`login-role-${r.key}`} onClick={() => pickRole(r.key)}
                    className={`flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold ring-1 transition-[background-color,color,box-shadow] duration-200 sm:text-sm ${on ? r.on : r.off}`}>
                    <Icon className="h-4 w-4" /> {r.label}
                  </button>
                );
              })}
            </div>}

            <div>
              <label htmlFor="phone" className="mb-1.5 block text-sm font-semibold text-blue-900/80">
                Mobile Number
              </label>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
                <input
                  data-testid="login-phone-input"
                  id="phone"
                  name="username"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="username"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your mobile number"
                  className="w-full rounded-xl border border-indigo-100 bg-[#F4F6FD] py-3 pl-11 pr-4 text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/70"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-blue-900/80">
                PIN
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-blue-400" />
                <input
                  data-testid="login-password-input"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your PIN"
                  className="w-full rounded-xl border border-indigo-100 bg-[#F4F6FD] py-3 pl-11 pr-11 text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/70"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-blue-500"
                  aria-label={showPassword ? "Hide PIN" : "Show PIN"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 py-3.5 text-sm font-semibold tracking-wide text-white shadow-lg shadow-blue-700/30 transition-[transform,box-shadow,background-color] hover:-translate-y-0.5 hover:from-blue-800 hover:to-blue-700 hover:shadow-xl hover:shadow-blue-700/30 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Login
                </>
              )}
            </button>
          </form>

          {(!linkRole || linkRole === "user") && <p className="mt-6 rounded-xl bg-[#FBF6EA] py-3 text-center text-sm text-slate-600 ring-1 ring-amber-100">
            New here?{" "}
            <Link
              data-testid="create-account-link"
              to="/register"
              className="font-bold text-blue-700 transition hover:text-blue-800 hover:underline"
            >
              Create Account
            </Link>
          </p>}
        </div>
      </motion.div>
    </div>
  );
}
